'use client'

import { type SyntheticEvent, useState } from 'react'

import { AlertBox } from '@/components/common/AlertBox'
import { ButtonDefault } from '@/components/common/Button'
import { Captcha } from '@/components/common/Captcha'
import { InputText, InputTextarea } from '@/components/common/Input'
import { type Dict } from '@/defs/misc'
import { fetchGAClientId } from '@/utils/analyticsUtil'
import { encodeArrayBufferToBase64, isValidEmail, parseError } from '@/utils/commonUtil'
import { fetchMailer } from '@/utils/requestUtil'

import styles from './FormMailer.module.scss'

type Props = {
  destination: string
  disableAttachment?: boolean
}

export const FormMailer = ({ destination, disableAttachment }: Props) => {
  const [attachment, setAttachment] = useState<string>()
  const [attachmentName, setAttachmentName] = useState('')
  const [attachmentType, setAttachmentType] = useState('')
  const [message, setMessage] = useState('')
  const [email, setEmail] = useState('')
  const [errors, setErrors] = useState<Dict<string>>({})
  const [loading, setLoading] = useState(false)
  const [notARobot, setNotARobot] = useState(false)
  const [success, setSuccess] = useState('')

  const onCaptcha = () => {
    setErrors(({ captcha: _, ...errors }) => errors)
    setNotARobot(true)
  }

  const onChangeMessage = (value: string) => {
    setErrors(({ message: _, ...errors }) => errors)
    setMessage(value)
  }

  const onChangeEmail = (value: string) => {
    setErrors(({ email: _, ...errors }) => errors)
    setEmail(value)
  }

  const onFileChosen = (e: React.ChangeEvent<HTMLInputElement>) => {
    setErrors(({ file: _, ...errors }) => errors)
    setAttachment(undefined)
    setAttachmentName('')
    setAttachmentType('')

    const file = e.target.files?.[0]
    if (!file) return
    if (file.size > 5 * 1024 * 1024) {
      return setErrors((errors) => ({ ...errors, file: 'Screenshots must be smaller than 5 MB' }))
    }

    const fileReader = new FileReader()
    fileReader.onloadend = () => setAttachment(encodeArrayBufferToBase64(fileReader.result as ArrayBuffer))
    fileReader.readAsArrayBuffer(file)

    setAttachmentName(file.name)
    setAttachmentType(file.type)
  }

  const getEmailConsent = (): Promise<boolean> =>
    new Promise((resolve) => {
      if (!window.__tcfapi) return resolve(true)
      let settled = false
      const done = (v: boolean) => {
        if (!settled) {
          settled = true
          resolve(v)
        }
      }
      window.__tcfapi('hasCustomConsent', 2, (res: boolean, success) => done(success ? res : true), 'email')
      setTimeout(() => done(true), 500)
    })

  const onSubmitForm = (e: SyntheticEvent<HTMLFormElement, SubmitEvent>) => {
    e.preventDefault()

    const newErrors: Dict<string> = {}
    if (!isValidEmail(email)) newErrors['email'] = 'Please enter a valid email address.'
    if (!message) newErrors['message'] = 'Please enter message.'
    if (!notARobot) newErrors['captcha'] = 'Captcha failed.'
    if (Object.keys(newErrors).length > 0) return setErrors(newErrors)

    setErrors({})
    setLoading(true)
    setSuccess('')

    void (async () => {
      try {
        const [hasEmailConsent, gaClientId] = await Promise.all([getEmailConsent(), fetchGAClientId()])
        const body: Parameters<typeof fetchMailer>[0] = {
          attachment,
          attachmentName,
          attachmentType,
          desc: message,
          email,
          hasEmailConsent,
          to: destination,
        }

        if (hasEmailConsent) {
          body.gaClientId = gaClientId
          body.history = {
            length: window.history.length,
            referrer: document.referrer,
            url: window.location.href,
          }
        }

        const data = await fetchMailer(body)
        if (data.errorMessage) {
          setErrors({ submit: `Error: ${data.errorMessage}` })
          return
        }
        setSuccess('Message received, thanks!')
      } catch (e) {
        setErrors({ submit: parseError(e, 'Error: Could not send email, please try again') })
      } finally {
        setLoading(false)
      }
    })()
  }

  return (
    <div className='embedded'>
      {success ? (
        <AlertBox variant='info'>
          <p>
            <strong>Submitted</strong>
          </p>
          <p>{success}</p>
        </AlertBox>
      ) : (
        <form onSubmit={onSubmitForm}>
          {errors.submit && (
            <AlertBox>
              <p>{errors.submit}</p>
            </AlertBox>
          )}
          <div className={styles.input}>
            {errors.email && (
              <AlertBox>
                <p>{errors.email}</p>
              </AlertBox>
            )}
            <InputText
              disabled={loading}
              label='Email'
              onChange={onChangeEmail}
              required
              size='lg'
              type='email'
              value={email}
            />
          </div>
          <div className={styles.input}>
            {errors.message && (
              <AlertBox>
                <p>{errors.message}</p>
              </AlertBox>
            )}
            <InputTextarea
              disabled={loading}
              onChange={onChangeMessage}
              placeholder='Message...'
              required
              size='lg'
              value={message}
            />
          </div>
          {!disableAttachment && (
            <div className={styles.input}>
              {errors.file && (
                <AlertBox>
                  <p>{errors.file}</p>
                </AlertBox>
              )}
              <h3 className={styles.label}>Attachment (optional)</h3>
              <div>
                <input aria-label='Attachment (optional)' disabled={loading} onChange={onFileChosen} type='file' />
              </div>
            </div>
          )}
          <div className={styles.input}>
            {errors.captcha && (
              <AlertBox>
                <p>{errors.captcha}</p>
              </AlertBox>
            )}
            <Captcha onCaptcha={onCaptcha} />
          </div>
          <div>
            <ButtonDefault disabled={loading} loading={loading} size='lg' type='submit'>
              Submit
            </ButtonDefault>
          </div>
        </form>
      )}
    </div>
  )
}
